import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Clock, User, Cpu, Radio, Tag, MessageSquare,
  AlertTriangle, Wrench, UserCheck, ChevronUp, CheckCircle2,
  XCircle, BookOpen, Loader2, Send, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import OperationsLayout, { PageHeader, SeverityBadge, StatusBadge } from './OperationsLayout';
import { getIncident, addEvent, closeIncident, promoteIncident } from '@/lib/operationsApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const EVENT_ICONS = {
  incident_opened:  { icon: AlertTriangle, color: "text-orange-500" },
  ai_suggestion:    { icon: MessageSquare, color: "text-blue-500" },
  engineer_action:  { icon: Wrench,        color: "text-yellow-500" },
  note:             { icon: MessageSquare, color: "text-muted-foreground" },
  escalation:       { icon: ChevronUp,     color: "text-red-500" },
  sme_feedback:     { icon: UserCheck,     color: "text-purple-500" },
  incident_closed:  { icon: CheckCircle2,  color: "text-green-500" },
  knowledge_promoted: { icon: BookOpen,    color: "text-green-600" },
  knowledge_reuse:  { icon: BookOpen,      color: "text-blue-400" },
};

const EVENT_TYPE_LABELS = {
  alarm: "Alarm", engineer_action: "Action", ai_suggestion: "AI Suggestion",
  sme_feedback: "SME Feedback", escalation: "Escalation", note: "Note",
};

export default function IncidentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [incident, setIncident] = useState(null);
  const [events, setEvents]     = useState([]);
  const [loading, setLoading]   = useState(true);

  // Add event form
  const [eventType, setEventType]       = useState("note");
  const [eventContent, setEventContent] = useState("");
  const [addingEvent, setAddingEvent]   = useState(false);

  // Close form
  const [showClose, setShowClose]   = useState(false);
  const [closeForm, setCloseForm]   = useState({
    root_cause: "", resolution: "", lessons_learned: "",
    escalated: false, escalation_level: "", first_time_fix: true,
  });
  const [closing, setClosing]       = useState(false);

  // Promote
  const [promoting, setPromoting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getIncident(id)
      .then(r => { setIncident(r.incident); setEvents(r.events || []); })
      .catch(e => { toast.error("Failed to load incident: " + e.message); navigate("/operations/incidents"); })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const handleAddEvent = async (e) => {
    e.preventDefault();
    if (!eventContent.trim()) return;
    setAddingEvent(true);
    try {
      await addEvent(id, { event_type: eventType, content: eventContent });
      setEventContent("");
      toast.success("Event added");
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAddingEvent(false);
    }
  };

  const handleClose = async (e) => {
    e.preventDefault();
    if (!closeForm.root_cause.trim() || !closeForm.resolution.trim()) {
      return toast.error("Root cause and resolution are required");
    }
    setClosing(true);
    try {
      await closeIncident(id, {
        ...closeForm,
        escalation_level: closeForm.escalated ? closeForm.escalation_level : null,
      });
      toast.success("Incident closed");
      setShowClose(false);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setClosing(false);
    }
  };

  const handlePromote = async () => {
    setPromoting(true);
    try {
      await promoteIncident(id);
      toast.success("Nominated for knowledge base — awaiting SME review");
      load();
    } catch (err) {
      toast.error(err.data?.detail || err.message);
    } finally {
      setPromoting(false);
    }
  };

  if (loading) {
    return (
      <OperationsLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </OperationsLayout>
    );
  }

  if (!incident) return null;

  const isClosed = incident.status === "closed";

  return (
    <OperationsLayout>
      <PageHeader
        title={incident.title}
        breadcrumb={["Incidents", incident.title?.slice(0, 40)]}
        actions={
          <Button variant="ghost" size="sm" onClick={() => navigate("/operations/incidents")}>
            <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back
          </Button>
        }
      />

      <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Left: Metadata */}
        <div className="space-y-4">
          {/* Identity */}
          <MetaCard title="Incident Details">
            <MetaRow icon={Tag}  label="Status">   <StatusBadge status={incident.status} /></MetaRow>
            <MetaRow icon={AlertTriangle} label="Severity"><SeverityBadge severity={incident.severity} /></MetaRow>
            <MetaRow icon={Radio} label="Vendor">  {incident.vendor || "—"}</MetaRow>
            <MetaRow icon={Cpu}   label="Tech">    {incident.technology || "—"}</MetaRow>
            <MetaRow icon={Cpu}   label="NF">      {incident.network_function || "—"}</MetaRow>
            <MetaRow icon={User}  label="Engineer">{incident.assigned_engineer?.slice(0, 20) || "—"}</MetaRow>
            <MetaRow icon={Clock} label="Opened">  {fmtDate(incident.created_at)}</MetaRow>
            {incident.resolved_at && (
              <MetaRow icon={CheckCircle2} label="Resolved">{fmtDate(incident.resolved_at)}</MetaRow>
            )}
            {incident.mttr_minutes != null && (
              <MetaRow icon={Clock} label="MTTR">{incident.mttr_minutes} min</MetaRow>
            )}
          </MetaCard>

          {/* Resolution (if closed) */}
          {isClosed && (
            <MetaCard title="Resolution">
              <div className="space-y-2">
                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-0.5">Root Cause</div>
                  <div className="text-sm text-foreground">{incident.root_cause}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-0.5">Resolution</div>
                  <div className="text-sm text-foreground">{incident.resolution}</div>
                </div>
                {incident.lessons_learned && (
                  <div>
                    <div className="text-xs font-medium text-muted-foreground mb-0.5">Lessons Learned</div>
                    <div className="text-sm text-foreground">{incident.lessons_learned}</div>
                  </div>
                )}
              </div>
            </MetaCard>
          )}

          {/* Knowledge promotion */}
          <MetaCard title="Knowledge">
            {incident.verified ? (
              <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                <CheckCircle2 className="w-4 h-4" />
                Verified — in knowledge base
              </div>
            ) : incident.knowledge_promotion_id ? (
              <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
                <BookOpen className="w-4 h-4" />
                Pending SME review
              </div>
            ) : isClosed ? (
              <Button
                size="sm"
                variant="outline"
                className="w-full"
                onClick={handlePromote}
                disabled={promoting}
              >
                {promoting
                  ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                  : <BookOpen className="w-3.5 h-3.5 mr-1" />
                }
                Nominate for Knowledge Base
              </Button>
            ) : (
              <div className="text-xs text-muted-foreground">Close incident first to promote</div>
            )}
          </MetaCard>
        </div>

        {/* Center: Timeline */}
        <div className="lg:col-span-2 space-y-4">

          {/* AI Diagnosis */}
          {incident.ai_diagnosis && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-medium text-blue-700 dark:text-blue-300">AI Initial Diagnosis</span>
              </div>
              <div className="text-sm text-foreground mb-2">
                <span className="font-medium">Probable cause:</span> {incident.ai_diagnosis.probable_cause}
              </div>
              {incident.ai_diagnosis.suggested_actions?.length > 0 && (
                <div className="mb-2">
                  <div className="text-xs font-medium text-muted-foreground mb-1">Suggested actions</div>
                  <ul className="space-y-0.5">
                    {incident.ai_diagnosis.suggested_actions.map((a, i) => (
                      <li key={i} className="text-xs text-foreground flex items-start gap-1.5">
                        <span className="text-blue-400 mt-0.5">→</span>{a}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {incident.ai_diagnosis.clarifying_questions?.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-1">Clarifying questions</div>
                  {incident.ai_diagnosis.clarifying_questions.map((q, i) => (
                    <div key={i} className="text-xs text-foreground">• {q}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Timeline */}
          <div className="bg-card border border-border rounded-lg">
            <div className="px-4 py-3 border-b border-border text-sm font-medium">
              Timeline <span className="text-xs text-muted-foreground font-normal ml-1">({events.length} events)</span>
            </div>
            <div className="divide-y divide-border max-h-96 overflow-y-auto">
              {events.map((ev) => {
                const cfg = EVENT_ICONS[ev.event_type] || { icon: MessageSquare, color: "text-muted-foreground" };
                const Icon = cfg.icon;
                return (
                  <div key={ev.id} className="px-4 py-3 flex gap-3">
                    <div className={`mt-0.5 flex-shrink-0 ${cfg.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-medium text-foreground capitalize">
                          {ev.event_type.replace(/_/g, " ")}
                        </span>
                        <span className="text-xs text-muted-foreground">{fmtDate(ev.created_at)}</span>
                        {ev.actor === "ai" && (
                          <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-1 rounded">AI</span>
                        )}
                      </div>
                      <div className="text-sm text-foreground">{ev.content}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Add event (only if not closed) */}
          {!isClosed && (
            <form onSubmit={handleAddEvent} className="bg-card border border-border rounded-lg p-4">
              <div className="text-sm font-medium mb-3">Add to Timeline</div>
              <div className="flex gap-2 mb-2">
                <select
                  className="h-8 text-sm border border-input rounded-md px-2 bg-background text-foreground"
                  value={eventType}
                  onChange={e => setEventType(e.target.value)}
                >
                  {Object.entries(EVENT_TYPE_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2">
                <Input
                  className="text-sm h-8"
                  placeholder="Describe what you did, found, or escalated…"
                  value={eventContent}
                  onChange={e => setEventContent(e.target.value)}
                />
                <Button type="submit" size="sm" disabled={addingEvent || !eventContent.trim()}>
                  {addingEvent ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                </Button>
              </div>
            </form>
          )}

          {/* Close incident */}
          {!isClosed && (
            <div className="bg-card border border-border rounded-lg p-4">
              {!showClose ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full border-green-200 text-green-700 hover:bg-green-50 dark:border-green-800 dark:text-green-400"
                  onClick={() => setShowClose(true)}
                >
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Close Incident
                </Button>
              ) : (
                <form onSubmit={handleClose} className="space-y-3">
                  <div className="text-sm font-medium">Close Incident</div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Root Cause *</label>
                    <textarea
                      className="w-full text-sm border border-input rounded-md px-3 py-2 bg-background text-foreground resize-none"
                      rows={3}
                      placeholder="What caused this incident?"
                      value={closeForm.root_cause}
                      onChange={e => setCloseForm(f => ({ ...f, root_cause: e.target.value }))}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Resolution *</label>
                    <textarea
                      className="w-full text-sm border border-input rounded-md px-3 py-2 bg-background text-foreground resize-none"
                      rows={3}
                      placeholder="How was it resolved?"
                      value={closeForm.resolution}
                      onChange={e => setCloseForm(f => ({ ...f, resolution: e.target.value }))}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Lessons Learned</label>
                    <textarea
                      className="w-full text-sm border border-input rounded-md px-3 py-2 bg-background text-foreground resize-none"
                      rows={2}
                      placeholder="What should we do differently next time?"
                      value={closeForm.lessons_learned}
                      onChange={e => setCloseForm(f => ({ ...f, lessons_learned: e.target.value }))}
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={closeForm.escalated}
                        onChange={e => setCloseForm(f => ({ ...f, escalated: e.target.checked }))}
                      />
                      Escalated
                    </label>
                    {closeForm.escalated && (
                      <select
                        className="h-7 text-sm border border-input rounded-md px-2 bg-background text-foreground"
                        value={closeForm.escalation_level}
                        onChange={e => setCloseForm(f => ({ ...f, escalation_level: e.target.value }))}
                      >
                        <option value="">Level</option>
                        <option>L1</option><option>L2</option><option>L3</option>
                      </select>
                    )}
                    <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={closeForm.first_time_fix}
                        onChange={e => setCloseForm(f => ({ ...f, first_time_fix: e.target.checked }))}
                      />
                      First-time fix
                    </label>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button type="button" variant="outline" size="sm" onClick={() => setShowClose(false)}>Cancel</Button>
                    <Button type="submit" size="sm" disabled={closing}>
                      {closing && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
                      Close Incident
                    </Button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </OperationsLayout>
  );
}

function MetaCard({ title, children }) {
  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="px-4 py-2.5 border-b border-border text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        {title}
      </div>
      <div className="px-4 py-3 space-y-2">{children}</div>
    </div>
  );
}

function MetaRow({ icon: Icon, label, children }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
      <span className="text-xs text-muted-foreground w-20 flex-shrink-0">{label}</span>
      <span className="text-sm text-foreground">{children}</span>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}
